import React from "react";
import { ArrowRight, Building2, Clock, Phone, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Hero() {
  return <section id="hero" data-nav="dark" className="relative min-h-[85vh] flex items-center justify-center bg-background text-foreground overflow-hidden py-16 md:py-24">
      {/* Background image container with technical scrim */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/commercial_hoa_property_restoration_trucks.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/commercial_hoa_property_restoration_trucks.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/commercial_hoa_property_restoration_trucks.png-wvc-sizes" alt="TriServe emergency commercial restoration trucks and crew deployed outside a multi-unit property" className="w-full h-full object-cover object-center" loading="eager" decoding="async" />
        {/* Dual-layer dark industrial scrim for high text legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-foreground/90 via-foreground/85 to-foreground/95" />
      </div>

      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Technical dispatch status bar */}
        <div className="flex flex-wrap items-center gap-3 mb-6 sm:mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/20 border border-primary/40 text-background text-xs font-mono font-medium tracking-wide">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" />
            </span>
            <span>24/7 RAPID DISPATCH</span>
            <span className="text-background/40">|</span>
            <span>MD · DC · NOVA</span>
          </div>

          <div className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background/10 border border-background/20 text-background/90 text-xs font-mono">
            <Building2 className="size-3.5 text-accent" />
            <span>COMMERCIAL & HOA SPECIALIZED</span>
          </div>
        </div>

        {/* Core content grid */}
        <div className="max-w-4xl">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-background tracking-tight leading-[1.1] mb-6">
            Commercial & HOA Property Restoration
          </h1>

          <p className="text-lg sm:text-xl md:text-2xl text-background/90 font-medium leading-snug mb-5">
            Rapid mitigation and rebuilds that protect tenants, stabilize operations, and keep your board fully informed across Maryland, DC, and Northern Virginia.
          </p>

          <p className="text-base sm:text-lg text-background/80 leading-relaxed max-w-3xl mb-8 sm:mb-10">
            When burst risers, roof breaches, or fire incidents threaten commercial facilities or condo communities, our direct local crews arrive ready to deploy heavy extraction, drying, and turn-key reconstruction under a single accountable contract. We coordinate directly with property managers, HOA boards, and insurance adjusters from the first hour through sign-off.
          </p>

          {/* Action triggers */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-10">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold text-base h-12 px-6 shadow-md border-0">
              <a href="tel:8554356631" className="inline-flex items-center justify-center gap-2">
                <Phone className="size-5 text-accent" />
                <span>Call Now</span>
                <span className="font-mono text-sm font-normal text-primary-foreground/90">(855) 435-6631</span>
              </a>
            </Button>

            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-bold text-base h-12 px-6 shadow-md border border-accent/40">
              <Link to="/ContactRequestServicePage" className="inline-flex items-center justify-center gap-2">
                <span>Request Service</span>
                <ArrowRight className="size-5" />
              </Link>
            </Button>
          </div>

          {/* Technical credentials hairline strip */}
          <div className="pt-6 border-t border-background/20 grid grid-cols-1 sm:grid-cols-3 gap-4 text-background/90 text-sm">
            <div className="flex items-center gap-2.5">
              <Clock className="size-4.5 text-accent shrink-0" />
              <span>Immediate on-site response 24/7</span>
            </div>
            <div className="flex items-center gap-2.5">
              <ShieldCheck className="size-4.5 text-accent shrink-0" />
              <span>Direct insurance adjuster coordination</span>
            </div>
            <div className="flex items-center gap-2.5">
              <Building2 className="size-4.5 text-accent shrink-0" />
              <span>Zero call centers — direct field leadership</span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}